/**
 * uploadProgress.js — global upload progress bus
 *
 * Sprint 2026-07 (A.2): lets any part of the app (Topbar, Sidebar, Bell
 * icon, share-target shim) observe the live state of the /upload page's
 * queue without either party importing the other. We use a `window`
 * CustomEvent because:
 *
 *   1. It works across React trees without a global provider refactor.
 *   2. It fires synchronously — the Topbar re-renders on the same tick
 *      the queue changes.
 *   3. It's zero-cost when nobody is listening.
 *
 * Payload shape (see `summary` in UploadPage.jsx):
 *   { total, queued, active, done, alerts, duplicate, error, alertsCount }
 *
 * Consumer usage:
 *   const p = useUploadProgress();
 *   if (p.active > 0) show <UploadProgressBadge total={p.total} done={p.done} />
 */
import { useEffect, useState } from 'react';

const EVENT_NAME = 'emb:upload-progress';

/** Publisher — call from UploadPage whenever `summary` changes. */
export function publishUploadProgress(summary) {
    if (typeof window === 'undefined') return;
    try {
        window.__embUploadProgress = summary;
        window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: summary }));
    } catch (e) {
        // Older browsers without CustomEvent constructor — silent fail.
    }
}

/** React hook — subscribe to upload progress. */
export function useUploadProgress() {
    const [state, setState] = useState(() => {
        if (typeof window !== 'undefined' && window.__embUploadProgress) {
            return window.__embUploadProgress;
        }
        return { total: 0, queued: 0, active: 0, done: 0, alerts: 0, duplicate: 0, error: 0, alertsCount: 0 };
    });
    useEffect(() => {
        const onEvt = (e) => setState(e.detail || {});
        window.addEventListener(EVENT_NAME, onEvt);
        return () => window.removeEventListener(EVENT_NAME, onEvt);
    }, []);
    return state;
}
